<template>
  <form-layout title="Set Job Time" @on-dismiss="dismiss">
    <vs-wrapper space="small">
      <vs-heading class="d-flex heading-overline" type="overline">Planned </vs-heading>
      <vs-date-picker
        :key="`${props.job.id}-start-time`"
        :can-select-past="true"
        :rules="[
          () => showStartTimeWarning && 'Start cannot be after end',
          () => isNaN(data.startTime.valueOf()) && 'This field is required',
        ]"
        :value="data.startTime"
        class="date-picker"
        data-test="planned-start-time"
        label="Start"
        required
        @input="handleStartTimeChange"
      />
      <vs-date-picker
        :key="`${props.job.id}-end-time`"
        :can-select-past="true"
        :rules="[
          () => showEndTimeWarning && 'End cannot be before start',
          () => isNaN(data.endTime.valueOf()) && 'This field is required',
        ]"
        :value="data.endTime"
        class="date-picker"
        data-test="planned-end-time"
        label="End"
        required
        @input="handleEndTimeChange"
      />
      <vs-text class="duration-line" type="caption">{{ plannedDurationLabel }} </vs-text>
      <v-spacer />
      <vs-heading class="heading-overline" type="overline">{{ completionLabel }} </vs-heading>
      <vs-date-picker
        :key="`${props.job.id}-activated-time`"
        :can-select-past="true"
        :rules="[
          () => showActivatedTimeWarning && 'Activated cannot be after completed',
          () => isNaN(data.activeTime.valueOf()) && 'This field is required',
        ]"
        :value="data.activeTime"
        class="date-picker"
        data-test="actual-active-time"
        label="Activated"
        required
        @input="handleActivatedTimeChange"
      />
      <vs-date-picker
        :key="`${props.job.id}-completed-time`"
        :can-select-past="true"
        :rules="[
          () => showCompletedTimeWarning && 'Completed cannot be before activated',
          () => isNaN(data.completedTime.valueOf()) && 'This field is required',
        ]"
        :value="data.completedTime"
        class="date-picker"
        data-test="actual-completed-time"
        label="Completed"
        required
        @input="handleCompletedTimeChange"
      />
      <vs-text class="duration-line" type="caption">{{ activeDurationLabel }} </vs-text>
      <v-spacer />
      <v-row no-gutters>
        <vs-button
          :disabled="saveButtonDisabled"
          :loading="data.saving"
          full-width
          label="Save"
          @click="save"
        />
      </v-row>
    </vs-wrapper>
    <template #snack-bar-wrapper>
      <vs-snackbar v-model="data.isSnackbarVisible" sticky type="error">
        Failed to update job details
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script lang="ts" setup>
import Job from '@/models/job'
import { computed, reactive, UnwrapNestedRefs } from 'vue'
import moment from 'moment'
import FormLayout from '@/components/common/FormLayout.vue'
import { formatDurationAsHours } from '@/utils/date-formatting'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useQueryClient } from '@tanstack/vue-query'

const props = defineProps<{
  job: Job
  jobHubId: string
}>()

type Data = UnwrapNestedRefs<{
  saving: boolean
  endTime: moment.Moment
  completedTime: moment.Moment
  activeTime: moment.Moment
  startTime: moment.Moment
  isSnackbarVisible: boolean
  error: boolean
}>

const data: Data = reactive({
  startTime: props.job.startTime,
  endTime: props.job.endTime,
  activeTime: moment(props.job.activeTime),
  completedTime: moment(props.job.completedTime),
  saving: false,
  isSnackbarVisible: false,
  error: false,
})

interface CompletedJobTimesPayload {
  id: string
  startTime: moment.Moment
  endTime: moment.Moment
  activeTime: moment.Moment
  completedTime: moment.Moment
}

const { updateCompletedJobActualTimes } = createNamespacedHelpers('jobs').useActions([
  'updateCompletedJobActualTimes',
])

const emits = defineEmits<{
  (event: 'updated'): void
}>()
const queryClient = useQueryClient()

const hasChanges = computed<boolean>(() => {
  return (
    !data.startTime.isSame(props.job.startTime) ||
    !data.endTime.isSame(props.job.endTime) ||
    !data.activeTime.isSame(moment(props.job.activeTime)) ||
    !data.completedTime.isSame(moment(props.job.completedTime))
  )
})

const showStartTimeWarning = computed<boolean>(() => {
  return data.startTime.isAfter(data.endTime) && !data.startTime.isSame(props.job.startTime)
})

const showEndTimeWarning = computed<boolean>(() => {
  return data.endTime.isBefore(data.startTime) && !data.endTime.isSame(props.job.endTime)
})

const showActivatedTimeWarning = computed<boolean>(() => {
  return (
    data.activeTime.isAfter(data.completedTime) &&
    !data.activeTime.isSame(moment(props.job.activeTime))
  )
})

const showCompletedTimeWarning = computed<boolean>(() => {
  return (
    data.completedTime.isBefore(data.activeTime) &&
    !data.completedTime.isSame(moment(props.job.completedTime))
  )
})

const showNaNWarning = computed<boolean>(() => {
  return (
    isNaN(data.startTime.valueOf()) ||
    isNaN(data.endTime.valueOf()) ||
    isNaN(data.activeTime.valueOf()) ||
    isNaN(data.completedTime.valueOf())
  )
})

const saveButtonDisabled = computed<boolean>(() => {
  return (
    !hasChanges.value ||
    showStartTimeWarning.value ||
    showEndTimeWarning.value ||
    showNaNWarning.value ||
    showActivatedTimeWarning.value ||
    showCompletedTimeWarning.value
  )
})

const formattedPlannedDuration = computed<string>(() => {
  if (
    isNaN(data.endTime.valueOf()) ||
    isNaN(data.startTime.valueOf()) ||
    showStartTimeWarning.value ||
    showEndTimeWarning.value
  ) {
    return '\u{2E3A}'
  }
  const estimatedDuration = moment.duration(data.endTime.diff(data.startTime))
  return formatDurationAsHours(estimatedDuration, true)
})

const formattedActiveDuration = computed<string>(() => {
  if (
    isNaN(data.activeTime.valueOf()) ||
    isNaN(data.completedTime.valueOf()) ||
    showActivatedTimeWarning.value ||
    showCompletedTimeWarning.value
  ) {
    return '\u{2E3A}'
  }
  const newDuration = moment.duration(data.completedTime.diff(data.activeTime))
  return formatDurationAsHours(newDuration, true)
})

const plannedDurationLabel = computed<string>(() => {
  return `Duration: ${formattedPlannedDuration.value}`
})

const activeDurationLabel = computed<string>(() => {
  return `Duration: ${formattedActiveDuration.value}`
})

const completionLabel = computed<string>(() => {
  return !props.job.activeTime ||
    moment(props.job.activeTime).isSame(moment(props.job.completedTime), 'minute')
    ? 'Completed'
    : 'Executed'
})

const handleStartTimeChange = (value: moment.Moment): void => {
  data.startTime = value
}

const handleEndTimeChange = (value: moment.Moment): void => {
  data.endTime = value
}

const handleActivatedTimeChange = (value: moment.Moment): void => {
  data.activeTime = value
}

const handleCompletedTimeChange = (value: moment.Moment): void => {
  data.completedTime = value
}

const save = async (): Promise<void> => {
  data.saving = true
  try {
    const updateCompletedJobActualTimesPayload: CompletedJobTimesPayload = {
      id: props.job.uuid,
      startTime: data.startTime,
      endTime: data.endTime,
      activeTime: data.activeTime,
      completedTime: data.completedTime,
    }
    await updateCompletedJobActualTimes(updateCompletedJobActualTimesPayload)
    await queryClient.invalidateQueries({
      queryKey: ['line-items', props.job.uuid],
    })
    emits('updated')
  } catch (err) {
    data.error = true
    data.saving = false
    return
  } finally {
    data.saving = false
    dismiss()
  }
}

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const dismiss = (): void => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isEditingPlannedActualTimesBottomSheetVisible: false,
  })
}
</script>
<style scoped>
.heading-overline {
  text-transform: none !important;
  font-size: 16px !important;
  margin-bottom: var(--space-smaller) !important;
}

.duration-line {
  font-style: italic !important;
  margin-left: var(--space-base) !important;
}
</style>
